import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import API from "../services/api";
import WhatsAppPreview from "../components/WhatsAppPreview";
import {reminderMessage, reminderStage} from "../services/whatsappService";
import Card from "../components/Card";
import PageHeader from "../components/PageHeader";
import Button from "../components/Button";
import { calendarDay } from "../calendarDay";

function Alerts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [whatsappDraft, setWhatsappDraft] = useState(null);
  const prepareReminder = credit => {
    const stage = reminderStage(credit) || 'upcoming';
    setWhatsappDraft({phone:credit.cliente?.telefono || '',text:reminderMessage(credit.cliente,credit,stage)});
  };
  const [credits, setCredits] = useState([]);
  const [overduePromises, setOverduePromises] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const section = searchParams.get("section");
    setExpanded(["overdue", "today", "week", "promises"].includes(section) ? section : null);
  }, [searchParams]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const creditsRes = await API.get("/credits");

      const promisesRes = await API.get("/collections/overdue/promises");

      setCredits(creditsRes.data);

      setOverduePromises(promisesRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const hoy = new Date();

  const morosos = credits.filter(
    (credit) => credit.activo === true && credit.saldoPendiente > 0 &&
      !['PAGADO','REFINANCIADO','CANCELADO'].includes(credit.estado) &&
      credit.fechaVencimiento && calendarDay(credit.fechaVencimiento) < calendarDay(hoy),
  );

  const vencenHoy = credits.filter((credit) => {
    if (!credit.fechaVencimiento) return false;

    const fecha = calendarDay(credit.fechaVencimiento);

    return (
      fecha === calendarDay(hoy) &&
      credit.activo === true &&
      credit.estado !== "PAGADO" &&
      credit.estado !== "REFINANCIADO" && credit.estado !== "CANCELADO"
    );
  });

  const vencenSemana = credits.filter((credit) => {
    if (!credit.fechaVencimiento) return false;

    const fecha = calendarDay(credit.fechaVencimiento);

    const diferencia = fecha - calendarDay(hoy);

    return (
      diferencia >= 0 &&
      diferencia <= 7 &&
      credit.activo === true &&
      credit.estado !== "PAGADO" &&
      credit.estado !== "REFINANCIADO" && credit.estado !== "CANCELADO"
    );
  });

  const formatMoney = (value) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value || 0);

  const renderCredit = (credit) => (
    <div
      key={credit._id}
      style={{
        padding: "12px 0",
        borderBottom: "1px solid #eee",
      }}
    >
      <strong>{credit.cliente ? `${credit.cliente.nombre} ${credit.cliente.apellido || ""}`.trim() : "Cliente no disponible"}</strong>
      <div className="cc-alert-credit-details">
        <div><span>Código del crédito</span><strong>{String(credit.nivelRefinanciacion || 0).padStart(2, "0")}-{String(credit.codigoDirecto || 0).padStart(4, "0")}</strong></div>
        <div><span>Estado</span><strong>{credit.estado}</strong></div>
        <div><span>Operador</span><strong>{credit.operador ? [credit.operador.nombre,credit.operador.apellido].filter(Boolean).join(' ') : 'Sin operador'}</strong></div>
        <div><span>Capital</span><strong>{formatMoney(credit.capital)}</strong></div>
        <div><span>Interés</span><strong>{formatMoney(credit.interes)}</strong></div>
        <div><span>Total pagado</span><strong>{formatMoney(credit.totalPagado)}</strong></div>
        <div><span>Cuotas</span><strong>{credit.cuotas} de {formatMoney(credit.valorCuota)}</strong></div>
        <div><span>Recargo acumulado</span><strong>{formatMoney(credit.recargoAcumulado)}</strong></div>
        <div><span>Recargo diario al activar mora</span><strong>{formatMoney(credit.recargoDiario)}</strong></div>
        <div><span>Monto total</span><strong>{formatMoney(credit.total)}</strong></div>
        <div><span>Vencimiento</span><strong>{credit.fechaVencimiento ? new Date(credit.fechaVencimiento).toLocaleDateString("es-AR") : "Sin fecha"}</strong></div>
        <div><span>Porcentaje pagado</span><strong>{(Number(credit.total) + Number(credit.recargoAcumulado || 0)) > 0 ? Math.min(100, Math.max(0, Math.round(Number(credit.totalPagado || 0) / (Number(credit.total) + Number(credit.recargoAcumulado || 0)) * 100))) : 0}%</strong></div>
        <div><span>Saldo pendiente</span><strong>{formatMoney(credit.saldoPendiente)}</strong></div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          marginTop: 10,
        }}
      >
        <Button onClick={() => navigate(`/client/${credit.cliente?._id}`)}>
          Ver Cliente
        </Button>

        <Button onClick={() => prepareReminder(credit)}>
          WhatsApp · Enviar aviso
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      {whatsappDraft && <WhatsAppPreview key={whatsappDraft.text} draft={whatsappDraft} onClose={() => setWhatsappDraft(null)} />}
      <PageHeader title="Alertas" subtitle="Seguimiento de cartera" />
      <div className="cc-alert-selectors">
        {[
          { id: "overdue", title: "🔴 Morosos", count: morosos.length },
          { id: "today", title: "🟠 Vencen Hoy", count: vencenHoy.length },
          { id: "week", title: "🟡 Vencen en 7 días", count: vencenSemana.length },
          { id: "promises", title: "🔴 Promesas Incumplidas", count: overduePromises.length },
        ].map(item => (
          <button key={item.id} id={`alert-toggle-${item.id}`} className="cc-card cc-alert-selector"
            aria-expanded={expanded === item.id} aria-controls={`alert-panel-${item.id}`}
            onClick={() => setExpanded(current => current === item.id ? null : item.id)}>
            <strong>{item.title}</strong><span>{item.count}</span>
            <small>{expanded === item.id ? "Plegar ▴" : "Desplegar ▾"}</small>
          </button>
        ))}
      </div>

      <div id="alert-panel-overdue" role="region" aria-labelledby="alert-toggle-overdue" hidden={expanded !== "overdue"}>
      <Card>
        <h2>🔴 Morosos ({morosos.length})</h2>

        {morosos.length === 0 ? (
          <p>No hay morosos</p>
        ) : (
          morosos.map(renderCredit)
        )}
      </Card>
      </div>

      <div id="alert-panel-today" role="region" aria-labelledby="alert-toggle-today" hidden={expanded !== "today"}>
      <Card>
        <h2>🟠 Vencen Hoy ({vencenHoy.length})</h2>

        {vencenHoy.length === 0 ? (
          <p>No hay vencimientos hoy</p>
        ) : (
          vencenHoy.map(renderCredit)
        )}
      </Card>
      </div>

      <div id="alert-panel-week" role="region" aria-labelledby="alert-toggle-week" hidden={expanded !== "week"}>
      <Card>
        <h2>🟡 Vencen en 7 días ({vencenSemana.length})</h2>

        {vencenSemana.length === 0 ? (
          <p>No hay vencimientos próximos</p>
        ) : (
          vencenSemana.map(renderCredit)
        )}
      </Card>
      </div>
      <div id="alert-panel-promises" role="region" aria-labelledby="alert-toggle-promises" hidden={expanded !== "promises"}>
      <Card>
        <h2>🔴 Promesas Incumplidas ({overduePromises.length})</h2>

        {overduePromises.length === 0 ? (
          <p>No hay promesas vencidas</p>
        ) : (
          overduePromises.map((item) => (
            <div
              key={item._id}
              style={{
                padding: "12px 0",
                borderBottom: "1px solid #eee",
              }}
            >
              <strong>{item.cliente?.nombre}</strong>

              <p>Monto: {formatMoney(item.montoCompromiso)}</p>

              <p>
                Fecha: {new Date(item.fechaCompromiso).toLocaleDateString()}
              </p>

              <Button onClick={() => navigate(`/client/${item.cliente?._id}`)}>
                Ver Cliente
              </Button>
            </div>
          ))
        )}
      </Card>
      </div>
    </div>
  );
}

export default Alerts;
